/**
 * COMMAND CENTER — Office-Day (CC-OFFICE-DAY-SPEC.md; approved mockup
 * mockups/command-center-office-day-v2.html). Personalized office-day view on
 * the /command-center-next route: aimed at the NEXT WORKING DAY being locked by
 * 4:30 today.
 *
 * The mockup's CSS is ported verbatim, scoped under `.ccod` (officeDayStyles),
 * so the page renders as approved without leaking into the app shell.
 *
 * LIVE, not a demo (Issue #129 — de-mock pass):
 *   · The view is driven ENTIRELY by the logged-in user's role
 *     (brief.me.viewProfile) — there is no "Preview as" toggle. Identity and
 *     lanes are always the real signed-in person.
 *   · "The Next Thing" is a real per-role queue: Do it navigates, skip →
 *     advances; an empty queue says "go live your day."
 *   · Assign controls are wired to POST /command-center/assign through a
 *     manager-only person-picker (assignees from the real team-load list; the
 *     target person is never hard-coded).
 *   · The 4:30 lock is a live countdown from the clock + the real still-to-stage
 *     count, not a canned string.
 *
 * HARD INVARIANT: estimates route to Corbin ONLY. They render as a locked
 * "🔒 → Corbin only" chip with no Assign control, and the server rejects any
 * estimate-assignment request (commandCenterNext POST /assign, kind:"estimate").
 */
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { officeDayCss } from "./officeDayStyles";
import { orgTodayKey } from "@/lib/datetime";
import { daysLeftOf, dueWords, rowTone, shortDue, type DeadlineRow } from "@/lib/deadlines";

type Persona = "regina" | "darcy" | "admin";
type Phase = "morning" | "run" | "stage";

interface AdminLaneItem {
    id: string;
    label: string;
    reason: string;
    href: string;
}
interface AdminBrief {
    quotes: {
        count: number;
        top: Array<{ id: string; label: string; blockedDays: number; value: number; href: string }>;
    };
    workOrders: { blockingCount: number; teachCount: number; top: AdminLaneItem[] };
    reports: { heldCount: number; rejectedCount: number; top: AdminLaneItem[] };
    docFixes: { count: number; top: Array<{ id: string; label: string; href: string }> };
    trackOrder: string[];
}

interface BlockingRow {
    jobId: string;
    kind: string;
    label: string;
    why: string;
    fixHref: string;
}

// CC-B: live field pace & predictions (Running mode).
interface AtRiskStop {
    jobId: string;
    label: string;
    scheduledStart: string | null;
}
interface TruckPace {
    techName: string;
    dot: "on_site" | "driving" | "idle" | "late" | "waiting";
    statusLine: string;
    paceLine: string;
    paceBad: boolean;
    stopN: number;
    stopM: number;
    slipMinutes: number;
    atRisk: AtRiskStop[];
    idleAvailable: boolean;
    currentLabel: string | null;
}
interface RunPrediction {
    techName: string;
    slipMinutes: number;
    stopN: number;
    stopM: number;
    currentLabel: string | null;
    atRisk: AtRiskStop[];
    absorbTech: string | null;
    gridHref: string;
}

interface Brief {
    me: {
        firstName: string | null;
        roleName: string | null;
        isManager: boolean;
        persona: "manager" | "front_office";
        viewProfile: "admin" | "manager" | "front_office";
        canAssign: boolean;
    };
    line: Array<{ key: string; count: number; delta: number | null }>;
    /** #2096: licenses, CE, city registrations, gauges, insurance and driver's
     *  licenses due within 30 days or out of date (services/deadlineRadar.ts). */
    radar?: DeadlineRow[];
    corbinGate: Array<{ what: string; detail: string; href: string }>;
    decisions: Array<{ what: string; detail: string; href: string }>;
    office: {
        tracks: {
            dispatch: { done: number; total: number; unconfirmed: number };
            prep: { done: number; total: number; blocked: number };
            closeout: { count: number };
            billing: { invoices: number; filings: number; open: number };
        };
        tomorrow: {
            name: string;
            label: string;
            jobsReady: number;
            jobsTotal: number;
            trucks: number;
            readyPct: number;
            stillNeed: number;
            blocking: BlockingRow[];
        };
        teamLoad: Array<{ id: string; name: string; role: string; assigned: number; done: number }>;
        myQueue: Array<{
            id: string;
            title: string;
            detail: string | null;
            href: string | null;
            fromName: string | null;
        }>;
        estimateCount: number;
        assignableCount: number;
    };
    handled: { filedToday: number; invoicedToday: number; collectedToday: number; preflightedJobs: number };
    admin: AdminBrief | null;
    run: { trucks: TruckPace[]; predictions: RunPrediction[]; idleTechs: string[] };
}

/** A payload the server's POST /command-center/assign accepts. Estimates are
 *  never built into one (Corbin-only), so `kind` is always safe. */
interface AssignPayload {
    kind: string;
    refId: string | null;
    title: string;
    detail: string;
    href: string | null;
}

/** One card in the hero "Next Thing" queue — assembled from real brief data. */
interface HeroItem {
    tag: string;
    tagTone: "teal" | "purple";
    wait: string;
    title: string;
    money: string;
    route: string;
    assign?: AssignPayload;
}

const ROLE_LABEL: Record<Persona, string> = {
    admin: "Admin · quotes & data integrity",
    regina: "Owner · Office Manager",
    darcy: "Front Office · dispatch & filing",
};

function greetWord(now = new Date()): string {
    const h = now.getHours();
    return h < 12 ? "Good morning" : h < 17 ? "Good afternoon" : "Good evening";
}
function autoPhase(now = new Date()): Phase {
    const h = now.getHours() + now.getMinutes() / 60;
    if (h < 9.5) return "morning";
    if (h < 15) return "run";
    return "stage";
}

// Locally-computed next working day — mirrors the server; used for labels
// before the brief lands (server confirms it in brief.office.tomorrow).
function localNextWorkingDay(): { name: string; label: string } {
    const names = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
    const mons = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    const n = new Date();
    do {
        n.setDate(n.getDate() + 1);
    } while (n.getDay() === 0 || n.getDay() === 6);
    return { name: names[n.getDay()], label: `${names[n.getDay()]} · ${mons[n.getMonth()]} ${n.getDate()}` };
}

const SUBS: Record<Phase, (day: string) => string> = {
    morning: () => "Morning Launch — get the guys out clean on what was staged yesterday.",
    run: () => "The Run — trucks are out. Clear closeout, billing & filing, and your gates.",
    stage: () => "Stage Tomorrow — the last window. Everything the guys need locks before 4:30.",
};

interface LockInfo {
    calm: boolean;
    time: string;
    badge: string;
    cap: string;
}

/** Live 4:30 lock — real time remaining + the real still-to-stage count. No
 *  canned strings. Once past 4:30 the next working day is set. */
function lockInfo(now: Date, stillNeed: number, day: string): LockInfo {
    const lock = new Date(now);
    lock.setHours(16, 30, 0, 0);
    const ms = lock.getTime() - now.getTime();
    if (ms <= 0) {
        return {
            calm: true,
            time: "Set",
            badge: "locked",
            cap:
                stillNeed > 0
                    ? `4:30 has passed — ${stillNeed} job${stillNeed === 1 ? "" : "s"} rolled into ${day} unstaged.`
                    : `${day}'s run is locked in and fully staged.`,
        };
    }
    const totalMin = Math.floor(ms / 60000);
    const h = Math.floor(totalMin / 60);
    const m = totalMin % 60;
    const time = h > 0 ? `${h}h ${String(m).padStart(2, "0")}m` : `${m}m`;
    const calm = stillNeed === 0 || totalMin > 90;
    const badge = stillNeed === 0 ? "all staged" : totalMin > 90 ? "runway left" : "closing soon";
    const cap =
        stillNeed === 0
            ? `Everything for ${day} is staged — nothing left before the lock.`
            : `to stage the last ${stillNeed} before the shop closes.`;
    return { calm, time, badge, cap };
}

/** Build the per-role "Next Thing" queue from real brief data. Order mirrors
 *  the server's ranking spirit: the person's own gate/work first, then the
 *  items that block the next working day. */
function buildHeroItems(role: Persona, brief: Brief | null): HeroItem[] {
    if (!brief) return [];
    const items: HeroItem[] = [];
    const tm = brief.office.tomorrow;

    if (role === "admin") {
        const aq = brief.admin;
        for (const q of aq?.quotes.top ?? []) {
            items.push({
                tag: "Your quote",
                tagTone: "teal",
                wait: "waiting on your review",
                title: q.label,
                money: `$${q.value.toFixed(0)} · blocked ${q.blockedDays} day${q.blockedDays === 1 ? "" : "s"}`,
                route: q.href,
            });
        }
        // Data-integrity work orders he alone maps.
        for (const w of aq?.workOrders.top ?? []) {
            items.push({
                tag: "Work order",
                tagTone: "teal",
                wait: "blocking a job",
                title: w.label,
                money: w.reason,
                route: w.href,
            });
        }
        // What's blocking the next working day — the admin stages tomorrow
        // too. Before this, Stage Tomorrow's "Next Thing" for the owner was
        // whatever document needed filing, with tomorrow nowhere on it
        // (dispatch audit C1).
        for (const b of tm.blocking) {
            items.push({
                tag: "Blocking tomorrow",
                tagTone: "purple",
                wait: `blocks ${tm.name}`,
                title: b.label,
                money: b.why,
                route: b.fixHref,
                assign: { kind: b.kind, refId: b.jobId, title: b.label, detail: b.why, href: b.fixHref },
            });
        }
        return items;
    }

    if (role === "regina") {
        // Her gate: releasing the next working day's dispatch, when there's a
        // day staged to release.
        if (tm.trucks > 0 || tm.jobsTotal > 0) {
            items.push({
                tag: "Your gate",
                tagTone: "teal",
                wait: "waiting on you",
                title: `Release ${tm.name}'s dispatch — ${tm.trucks} truck${tm.trucks === 1 ? "" : "s"}`,
                money:
                    tm.trucks > 0
                        ? `${tm.trucks} route${tm.trucks === 1 ? "" : "s"} staged and ready — waiting on your go.`
                        : `${tm.jobsTotal} job${tm.jobsTotal === 1 ? "" : "s"} on ${tm.name} — none routed to a truck yet.`,
                route: "/dispatch",
            });
        }
        // Open decisions she owns (assignable to front office).
        for (const d of brief.decisions) {
            items.push({
                tag: "Decision",
                tagTone: "purple",
                wait: "waiting on you",
                title: d.what,
                money: d.detail,
                route: d.href,
                assign: { kind: "decision", refId: null, title: d.what, detail: d.detail, href: d.href },
            });
        }
        // What's blocking the next working day (assignable).
        for (const b of tm.blocking) {
            items.push({
                tag: "Blocking tomorrow",
                tagTone: "purple",
                wait: `blocks ${tm.name}`,
                title: b.label,
                money: b.why,
                route: b.fixHref,
                assign: { kind: b.kind, refId: b.jobId, title: b.label, detail: b.why, href: b.fixHref },
            });
        }
        return items;
    }

    // Darcy — front office. Her queue is what's been assigned to her.
    for (const m of brief.office.myQueue) {
        items.push({
            tag: m.fromName ? `From ${m.fromName}` : "Assigned",
            tagTone: "purple",
            wait: "assigned to you",
            title: m.title,
            money: m.detail ?? (m.fromName ? `Handed to you by ${m.fromName}.` : "Assigned to you."),
            route: m.href ?? "/tasks",
        });
    }
    return items;
}

export default function CommandCenterNextPage() {
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const briefQ = useQuery({
        queryKey: ["command-center-brief"],
        queryFn: () => api.get<Brief>("/api/command-center/brief"),
        refetchInterval: 60_000,
    });
    const brief = briefQ.data ?? null;

    // The view is the logged-in user's — resolved server-side, no toggle.
    const role: Persona = brief
        ? brief.me.viewProfile === "admin"
            ? "admin"
            : brief.me.viewProfile === "front_office"
              ? "darcy"
              : "regina"
        : "regina";
    const canAssign = brief?.me.canAssign ?? false;

    const [phase, setPhase] = useState<Phase>(autoPhase());
    const [heroIdx, setHeroIdx] = useState(0);
    const [assignFor, setAssignFor] = useState<AssignPayload | null>(null);
    // Predictions the viewer chose to "let ride" this session (by tech).
    const [ridden, setRidden] = useState<Set<string>>(() => new Set());

    // A minute tick so the live 4:30 countdown stays honest between refetches.
    const [now, setNow] = useState(() => new Date());
    useEffect(() => {
        const id = setInterval(() => setNow(new Date()), 30_000);
        return () => clearInterval(id);
    }, []);

    const assignMut = useMutation({
        mutationFn: (vars: { payload: AssignPayload; assigneeId: string }) =>
            api.post("/api/command-center/assign", { ...vars.payload, assigneeId: vars.assigneeId }),
        onSuccess: () => {
            setAssignFor(null);
            void queryClient.invalidateQueries({ queryKey: ["command-center-brief"] });
        },
    });

    const heroItems = useMemo(() => buildHeroItems(role, brief), [role, brief]);
    // Reset the skip cursor when the viewer's role resolves/changes.
    useEffect(() => {
        setHeroIdx(0);
    }, [role]);

    const day = brief?.office?.tomorrow.name ?? localNextWorkingDay().name;
    const dayLabel = brief?.office?.tomorrow.label ?? localNextWorkingDay().label;
    const t = brief?.office?.tracks;
    const tm = brief?.office?.tomorrow;
    const trucks = tm?.trucks ?? 0;
    const aq = brief?.admin ?? null;

    // CC-B Running mode: the live truck strip, and the top unheeded prediction
    // that should lead the page while a truck is behind plan.
    const run = brief?.run ?? null;
    const runLead = phase === "run" ? ((run?.predictions ?? []).find((p) => !ridden.has(p.techName)) ?? null) : null;

    const heroItem = heroIdx < heroItems.length ? heroItems[heroIdx] : null;
    const heroLeft = Math.max(0, heroItems.length - heroIdx - 1);

    const personalHint =
        role === "admin"
            ? "Ordered for your day — quotes first, then work orders & reports."
            : role === "regina"
              ? "Ordered for the office manager — billing & closeout up top."
              : "Ordered for front office — dispatch & filing up top.";

    const effectiveOrder =
        role === "admin" && aq?.trackOrder
            ? aq.trackOrder
            : role === "regina"
              ? ["billing", "closeout", "dispatch", "prep"]
              : ["dispatch", "prep", "billing", "closeout"];
    const trackOrder = (lane: string) => ({ order: effectiveOrder.indexOf(lane) });
    const lock = lockInfo(now, tm?.stillNeed ?? 0, day);
    const collectedK = brief ? `$${(brief.handled.collectedToday / 1000).toFixed(1)}k` : "$0k";

    // Decisions tile (manager) — real gate + assignable decisions, no invented rows.
    const estGate = brief?.corbinGate?.find((g) => g.what.startsWith("Estimate")) ?? null;
    const assignableDecisions = brief?.decisions ?? [];
    const decCount = (brief?.office?.estimateCount ?? 0) + (brief?.office?.assignableCount ?? 0);
    const maxAssigned = Math.max(1, ...(brief?.office?.teamLoad ?? []).map((p) => p.assigned));
    const team = brief?.office?.teamLoad ?? [];

    const openAssign = (payload: AssignPayload) => setAssignFor(payload);

    return (
        <div className="ccod" data-role={role}>
            <style>{officeDayCss}</style>

            {/* The mockup's topbar + WORK PULSE strip are app-shell chrome the
                real shell already renders above this page. The page owns
                everything from the identity row down. */}
            <div className="wrap">
                {/* identity — always the logged-in user (no preview toggle). */}
                <div className="idrow">
                    <div className="who-am-i">
                        <div className={`avatar ${role}`}>{(brief?.me.firstName ?? "?").charAt(0).toUpperCase()}</div>
                        <div>
                            <div className="n">{brief?.me.firstName ?? "…"}</div>
                            <div className="r">{brief?.me.roleName ?? ROLE_LABEL[role]}</div>
                        </div>
                    </div>
                </div>

                <div className="cc-head">
                    <div>
                        <div className="cc-title">
                            {greetWord()}, {brief?.me.firstName ?? "there"}.
                        </div>
                        <div className="cc-sub">
                            {role === "admin"
                                ? "Your quotes and the mappings that make or break a job."
                                : SUBS[phase](day)}
                        </div>
                        <div className="personal-hint">✦ {personalHint}</div>
                    </div>
                    <div className="phase-toggle">
                        {(
                            [
                                ["morning", "Morning Launch", "8:00–9:30"],
                                ["run", "The Run", "9:30–3:00"],
                                ["stage", "Stage Tomorrow", "3:00–4:30"],
                            ] as Array<[Phase, string, string]>
                        ).map(([p, label, tmv]) => (
                            <button
                                key={p}
                                className={phase === p ? "on" : ""}
                                onClick={() => setPhase(p)}
                                title={
                                    p === "stage"
                                        ? "Stage Tomorrow — the end-of-day pass that gets tomorrow's jobs ready before everyone leaves"
                                        : undefined
                                }
                            >
                                {label}
                                <span className="tm">{tmv}</span>
                            </button>
                        ))}
                    </div>
                </div>

                <div className="grid">
                    {/* MAIN */}
                    <div className="col">
                        {/* CC-B Running mode: a real pace prediction leads while a
                            truck is behind plan; otherwise the Next-Thing hero. */}
                        {runLead && (
                            <PredictionHero
                                pred={runLead}
                                navigate={navigate}
                                onLetRide={() =>
                                    setRidden((s) => {
                                        const next = new Set(s);
                                        next.add(runLead.techName);
                                        return next;
                                    })
                                }
                            />
                        )}

                        {/* HERO — the real "Next Thing" queue */}
                        {!runLead && (
                            <div className="card hero">
                                <div className="eyebrow">
                                    THE NEXT THING{" "}
                                    <span className="rest">
                                        {heroItem
                                            ? heroLeft > 0
                                                ? `· ${heroLeft} more after this`
                                                : "· last one"
                                            : "· all clear"}
                                    </span>
                                </div>
                                {heroItem ? (
                                    <>
                                        <span className="waiting">{heroItem.wait}</span>
                                        <span
                                            className="tag"
                                            style={{
                                                background:
                                                    heroItem.tagTone === "purple"
                                                        ? "var(--purple-soft)"
                                                        : "var(--teal-soft)",
                                                color: heroItem.tagTone === "purple" ? "var(--purple)" : "var(--teal)",
                                            }}
                                        >
                                            {heroItem.tag}
                                        </span>
                                        <h2>{heroItem.title}</h2>
                                        <div className="money">{heroItem.money}</div>
                                        <div className="actions">
                                            <button className="btn-do" onClick={() => navigate(heroItem.route)}>
                                                Do it
                                            </button>
                                            {canAssign && heroItem.assign && (
                                                <button
                                                    className="btn-assign"
                                                    onClick={() => openAssign(heroItem.assign!)}
                                                >
                                                    Assign…
                                                </button>
                                            )}
                                            <button className="btn-skip" onClick={() => setHeroIdx((i) => i + 1)}>
                                                skip →
                                            </button>
                                            <span className="route">→ {heroItem.route}</span>
                                        </div>
                                    </>
                                ) : (
                                    <>
                                        <span
                                            className="tag"
                                            style={{ background: "var(--teal-soft)", color: "var(--teal)" }}
                                        >
                                            {briefQ.isLoading ? "Loading" : "All clear"}
                                        </span>
                                        <h2>
                                            {briefQ.isLoading
                                                ? "Pulling your day together…"
                                                : heroItems.length > 0
                                                  ? "That's the whole queue — go live your day."
                                                  : "Nothing waiting on you — go live your day."}
                                        </h2>
                                        <div className="money">
                                            {briefQ.isLoading
                                                ? "One moment."
                                                : "The Next Thing is empty. It'll fill back in as work lands."}
                                        </div>
                                        {!briefQ.isLoading && heroItems.length > 0 && (
                                            <div className="actions">
                                                <button className="btn-skip" onClick={() => setHeroIdx(0)}>
                                                    ↺ start over
                                                </button>
                                            </div>
                                        )}
                                    </>
                                )}
                            </div>
                        )}

                        {/* CC-B live field strip — per-truck pace vs plan (Running mode). */}
                        {phase === "run" && (run?.trucks.length ?? 0) > 0 && <LiveField trucks={run!.trucks} />}

                        {role === "admin" ? (
                            <AdminLanes
                                aq={aq}
                                trackStyle={trackOrder}
                                personalHint={personalHint}
                                navigate={navigate}
                            />
                        ) : (
                            <>
                                {/* TRACKS */}
                                <div className="tracks-h">
                                    FOUR TRACKS TO A CLEAN TOMORROW ·{" "}
                                    <b>what has to be done today so {day} launches on time</b>
                                </div>
                                <div className="tracks">
                                    <div className="track dispatch" style={trackOrder("dispatch")}>
                                        <div className="k">
                                            Dispatch <span className="mine show-darcy">yours</span>
                                        </div>
                                        <div className="n">
                                            {t?.dispatch.done ?? 0}
                                            <small>/{t?.dispatch.total ?? 0}</small>
                                        </div>
                                        <div className="bar">
                                            <i style={{ width: `${pct(t?.dispatch.done, t?.dispatch.total)}%` }} />
                                        </div>
                                        <div className="note">
                                            <b>{t?.dispatch.unconfirmed ?? 0} routes</b> not confirmed to techs yet.
                                        </div>
                                        <span className="flag">{t?.dispatch.unconfirmed ?? 0} to send</span>
                                    </div>
                                    <div className="track prep" style={trackOrder("prep")}>
                                        <div className="k">
                                            Prep <span className="mine show-darcy">yours</span>
                                        </div>
                                        <div className="n">
                                            {t?.prep.done ?? 0}
                                            <small>/{t?.prep.total ?? 0}</small>
                                        </div>
                                        <div className="bar">
                                            <i style={{ width: `${pct(t?.prep.done, t?.prep.total)}%` }} />
                                        </div>
                                        <div className="note">
                                            <b>{t?.prep.blocked ?? 0} jobs</b> waiting on parts / permit.
                                        </div>
                                        <span className="flag">{t?.prep.blocked ?? 0} blocked</span>
                                    </div>
                                    <div className="track closeout" style={trackOrder("closeout")}>
                                        <div className="k">Closeout</div>
                                        <div className="n">{t?.closeout.count ?? 0}</div>
                                        <div className="bar">
                                            <i
                                                style={{
                                                    width: `${Math.min(100, (t?.closeout.count ?? 0) >= 30 ? 32 : 70)}%`,
                                                }}
                                            />
                                        </div>
                                        <div className="note">
                                            {(t?.closeout.count ?? 0) >= 30 ? (
                                                <>
                                                    Jammed — clear today or it rolls into <b>{day}</b>.
                                                </>
                                            ) : (
                                                <>
                                                    Flowing — keep it clear before <b>{day}</b>.
                                                </>
                                            )}
                                        </div>
                                        <span className="flag">
                                            {(t?.closeout.count ?? 0) >= 30 ? "jamming" : "flowing"}
                                        </span>
                                    </div>
                                    <div className="track billing" style={trackOrder("billing")}>
                                        <div className="k">Billing &amp; Filing</div>
                                        <div className="n">{t?.billing.open ?? 0}</div>
                                        <div className="bar">
                                            <i
                                                style={{
                                                    width: `${pct(
                                                        t?.billing.invoices ?? 0,
                                                        (t?.billing.invoices ?? 0) + (t?.billing.filings ?? 0),
                                                    )}%`,
                                                }}
                                            />
                                        </div>
                                        <div className="note">
                                            <b>{t?.billing.invoices ?? 0} invoices</b> + {t?.billing.filings ?? 0}{" "}
                                            filings open.
                                        </div>
                                        <span className="flag">{t?.billing.open ?? 0} open</span>
                                    </div>
                                </div>

                                {/* tiles */}
                                <div className="tiles">
                                    <DeadlineRadarTile rows={brief?.radar ?? []} onOpen={(href) => navigate(href)} />
                                    <div className="tile">
                                        {role === "regina" ? (
                                            <>
                                                <div className="th">
                                                    <span className="name">Decisions to assign</span>
                                                    <span className="cnt">{decCount}</span>
                                                </div>
                                                <div className="body">
                                                    {estGate && (
                                                        <div className="row">
                                                            <span>{trim(estGate.what)}</span>
                                                            <span className="lock-chip">🔒 → Corbin only</span>
                                                        </div>
                                                    )}
                                                    {assignableDecisions.slice(0, estGate ? 1 : 2).map((d, i) => (
                                                        <div className="row" key={i}>
                                                            <span>{trim(d.what)}</span>
                                                            {canAssign ? (
                                                                <button
                                                                    className="assign-chip"
                                                                    onClick={() =>
                                                                        openAssign({
                                                                            kind: "decision",
                                                                            refId: null,
                                                                            title: d.what,
                                                                            detail: d.detail,
                                                                            href: d.href,
                                                                        })
                                                                    }
                                                                >
                                                                    Assign…
                                                                </button>
                                                            ) : (
                                                                <b>→</b>
                                                            )}
                                                        </div>
                                                    ))}
                                                    {decCount === 0 && (
                                                        <span className="empty">✓ No decisions waiting.</span>
                                                    )}
                                                    {(() => {
                                                        const shown =
                                                            (estGate ? 1 : 0) +
                                                            Math.min(assignableDecisions.length, estGate ? 1 : 2);
                                                        const more = decCount - shown;
                                                        return more > 0 ? (
                                                            <div className="row">
                                                                <span>+ {more} more in the flow above</span>
                                                                <b>→</b>
                                                            </div>
                                                        ) : null;
                                                    })()}
                                                </div>
                                            </>
                                        ) : (
                                            <>
                                                <div className="th">
                                                    <span className="name">Your filing queue</span>
                                                    <span className="cnt">{t?.billing.filings ?? 0}</span>
                                                </div>
                                                <div className="body">
                                                    {(brief?.office?.myQueue ?? []).slice(0, 2).map((m) => (
                                                        <div className="row" key={m.id}>
                                                            <span>{trim(m.title)}</span>
                                                            <button
                                                                className="assign-chip"
                                                                style={{
                                                                    background: "var(--teal-soft)",
                                                                    color: "var(--teal)",
                                                                }}
                                                                onClick={() => m.href && navigate(m.href)}
                                                            >
                                                                do
                                                            </button>
                                                        </div>
                                                    ))}
                                                    {(brief?.office?.myQueue.length ?? 0) === 0 && (
                                                        <span className="empty">✓ Nothing assigned to you.</span>
                                                    )}
                                                    {(brief?.office?.myQueue.length ?? 0) > 2 && (
                                                        <div className="row">
                                                            <span>
                                                                + {(brief?.office?.myQueue.length ?? 0) - 2} more
                                                                assigned to you
                                                            </span>
                                                            <b>→</b>
                                                        </div>
                                                    )}
                                                </div>
                                            </>
                                        )}
                                    </div>
                                </div>
                            </>
                        )}
                    </div>

                    {/* SIDE */}
                    <div className="col">
                        {/* ADMIN — quotes-to-review list (his side rail) */}
                        {role === "admin" && (
                            <div className="card team">
                                <div className="hh">
                                    <span>Quotes to review</span>
                                    <span style={{ color: "var(--muted-2)", fontWeight: 700 }}>
                                        {aq?.quotes.count ?? 0} open
                                    </span>
                                </div>
                                {(aq?.quotes.top ?? []).map((q, i) => (
                                    <div className="person" key={q.id}>
                                        <div
                                            className="a"
                                            style={{
                                                background: ["var(--teal)", "var(--amber)", "var(--slate)"][i % 3],
                                            }}
                                        >
                                            {i + 1}
                                        </div>
                                        <div className="pn">
                                            {q.label}
                                            <small>
                                                ${q.value.toFixed(0)} · blocked {q.blockedDays}d
                                            </small>
                                        </div>
                                        <button
                                            className="go"
                                            style={{ border: 0, cursor: "pointer" }}
                                            onClick={() => navigate(q.href)}
                                        >
                                            open
                                        </button>
                                    </div>
                                ))}
                                {(aq?.quotes.count ?? 0) === 0 && (
                                    <div className="person">
                                        <div className="pn" style={{ color: "var(--muted-2)" }}>
                                            ✓ Every quote is reviewed.
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* TOMORROW — rendered for EVERY persona. The dispatch
                            audit's worst Command Center finding (C1): the owner
                            walks as admin, this card was gated to non-admin
                            personas, so "Stage Tomorrow · 3:00–4:30" showed him
                            nothing about tomorrow at all. The readiness ring,
                            the blocking list, and the fix links ARE the Stage
                            Tomorrow screen — never hide them from the person
                            staging the day. */}
                        {
                            <>
                                <div className="card tomorrow">
                                    <div className="head">
                                        <span className="lab">Next Working Day</span>
                                        <span className="day">{dayLabel}</span>
                                    </div>
                                    <h3>Is {day} ready?</h3>
                                    <div className="ready">
                                        <div
                                            className="ring"
                                            style={{ "--p": tm?.readyPct ?? 0 } as React.CSSProperties}
                                        >
                                            <div className="pct">
                                                <b>{tm?.readyPct ?? 0}%</b>
                                                <span>STAGED</span>
                                            </div>
                                        </div>
                                        <div className="who">
                                            <span className="big">
                                                {tm?.jobsReady ?? 0}{" "}
                                                <span style={{ fontWeight: 600, fontSize: 14, color: "var(--muted)" }}>
                                                    of {tm?.jobsTotal ?? 0} jobs
                                                </span>
                                            </span>
                                            ready to roll · <b>{trucks} trucks</b>
                                            <br />
                                            {(tm?.stillNeed ?? 0) > 0 ? (
                                                <span style={{ color: "var(--red)", fontWeight: 700 }}>
                                                    {tm?.stillNeed} still need action today.
                                                </span>
                                            ) : (
                                                <span style={{ color: "var(--teal)", fontWeight: 700 }}>
                                                    ✓ Everything's staged.
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                    <div className="block-list">
                                        <div className="bh">
                                            <span>Blocking tomorrow</span>
                                            {canAssign && (tm?.blocking.length ?? 0) > 0 && (
                                                <span className="sub">tap Assign to hand off</span>
                                            )}
                                        </div>
                                        {(tm?.blocking ?? []).slice(0, 3).map((b) => (
                                            <div className="b-row" key={b.jobId + b.kind}>
                                                <span className="l">
                                                    {trim(b.label)} <span className="why">· {trim(b.why, 22)}</span>
                                                </span>
                                                <span className="actions">
                                                    {canAssign && (
                                                        <button
                                                            className="assign-chip"
                                                            onClick={() =>
                                                                openAssign({
                                                                    kind: b.kind,
                                                                    refId: b.jobId,
                                                                    title: b.label,
                                                                    detail: b.why,
                                                                    href: b.fixHref,
                                                                })
                                                            }
                                                        >
                                                            Assign…
                                                        </button>
                                                    )}
                                                    <button className="go" onClick={() => navigate(b.fixHref)}>
                                                        fix
                                                    </button>
                                                </span>
                                            </div>
                                        ))}
                                        {(tm?.blocking.length ?? 0) > 3 && (
                                            <div className="b-row">
                                                <span className="l">
                                                    + {(tm?.blocking.length ?? 0) - 3} more{" "}
                                                    <span className="why">· open the run to see them</span>
                                                </span>
                                                <button className="go" onClick={() => navigate("/dispatch")}>
                                                    →
                                                </button>
                                            </div>
                                        )}
                                        {(tm?.blocking.length ?? 0) === 0 && (
                                            <div className="b-row">
                                                <span className="l" style={{ color: "var(--teal)" }}>
                                                    ✓ Nothing blocking {day}.
                                                </span>
                                            </div>
                                        )}
                                    </div>
                                    <div className={lock.calm ? "lock calm" : "lock"}>
                                        <div className="lh">
                                            <span className="t">Locks at 4:30 PM</span>
                                            <span className="badge">{lock.badge}</span>
                                        </div>
                                        <div className="time">{lock.time}</div>
                                        <div className="cap">{lock.cap}</div>
                                    </div>
                                    {/* The mission on this card is staging; this is the one button
                                        to the actual Parts-to-Stage sheet (grouped by tech, device
                                        photos, print/CSV). It used to be reachable only from the
                                        ≣ More menu / ⌘K — the office saw the countdown here but had
                                        to hunt for the tool. */}
                                    <button
                                        className="btn-do"
                                        style={{
                                            width: "100%",
                                            marginTop: 14,
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            gap: 8,
                                        }}
                                        onClick={() => navigate("/reports/parts-to-stage")}
                                    >
                                        {lock.calm ? "Review the staging sheet" : "Open the staging sheet"} →
                                    </button>
                                </div>

                                {/* team load (Regina) */}
                                <div className="card team r-only">
                                    <div className="hh">
                                        <span>Team load · today</span>
                                        <span style={{ color: "var(--muted-2)", fontWeight: 700 }}>
                                            assigned · done
                                        </span>
                                    </div>
                                    {team.map((p) => (
                                        <div className="person" key={p.id}>
                                            <div
                                                className="a"
                                                style={{ background: p.role === "admin" ? "#6b5b95" : "#3f7d6e" }}
                                            >
                                                {(p.name || "?").charAt(0)}
                                            </div>
                                            <div className="pn">
                                                {p.name}
                                                <small>{p.role}</small>
                                            </div>
                                            <div className="loadbar">
                                                <div className="lb">
                                                    <i
                                                        style={{
                                                            width: `${Math.round((p.assigned / maxAssigned) * 100)}%`,
                                                        }}
                                                    />
                                                </div>
                                                <div className="lt">
                                                    {p.assigned} assigned · {p.done} done
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                    {team.length === 0 && (
                                        <div className="person">
                                            <div className="pn" style={{ color: "var(--muted-2)" }}>
                                                No office staff loaded yet.
                                            </div>
                                        </div>
                                    )}
                                </div>

                                {/* my queue (Darcy) */}
                                <div className="card team d-only">
                                    <div className="hh">
                                        <span>Assigned to you</span>
                                        <span style={{ color: "var(--muted-2)", fontWeight: 700 }}>
                                            {brief?.office?.myQueue.length ?? 0} open
                                        </span>
                                    </div>
                                    {(brief?.office?.myQueue ?? []).slice(0, 4).map((m, i) => (
                                        <div className="person" key={m.id}>
                                            <div
                                                className="a"
                                                style={{
                                                    background: [
                                                        "var(--teal)",
                                                        "var(--amber)",
                                                        "var(--slate)",
                                                        "var(--purple)",
                                                    ][i % 4],
                                                }}
                                            >
                                                {i + 1}
                                            </div>
                                            <div className="pn">
                                                {m.title}
                                                <small>
                                                    {m.detail ?? (m.fromName ? `from ${m.fromName}` : "assigned")}
                                                </small>
                                            </div>
                                            <button
                                                className="go"
                                                style={{ border: 0, cursor: "pointer" }}
                                                onClick={() => m.href && navigate(m.href)}
                                            >
                                                do
                                            </button>
                                        </div>
                                    ))}
                                    {(brief?.office?.myQueue.length ?? 0) === 0 && (
                                        <div className="person">
                                            <div className="pn" style={{ color: "var(--muted-2)" }}>
                                                Nothing assigned to you right now.
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </>
                        }

                        {/* handled — in Stage Tomorrow the header pivots so the
                            panel doesn't read as today-mode furniture on the
                            tomorrow screen (dispatch audit C1/m5). */}
                        <div className="card handled">
                            <div className="hh">
                                {phase === "stage" ? "Today's done — now stage tomorrow" : "Handled for you today"}
                            </div>
                            <div className="stat-strip">
                                <div className="stat">
                                    <b>{brief?.handled.preflightedJobs ?? 0}</b>
                                    <span title="Jobs checked before dispatch — kit, devices, provider, and contact verified">
                                        pre-flighted
                                    </span>
                                </div>
                                <div className="stat">
                                    <b>{brief?.handled.filedToday ?? 0}</b>
                                    <span>filed</span>
                                </div>
                                <div className="stat">
                                    <b>{brief?.handled.invoicedToday ?? 0}</b>
                                    <span>invoiced</span>
                                </div>
                                <div className="stat">
                                    <b>{collectedK}</b>
                                    <span>collected</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Assign person-picker — manager-only, lane-generic (never hard-codes
                the target person; assignees are the real team-load list). */}
            {assignFor && (
                <div className="assign-pop-backdrop" onClick={() => setAssignFor(null)}>
                    <div className="assign-pop" onClick={(e) => e.stopPropagation()}>
                        <div className="ap-h">
                            Assign <b>{trim(assignFor.title, 40)}</b> to…
                        </div>
                        <div className="ap-list">
                            {team.length === 0 && <div className="ap-empty">No office staff to assign to yet.</div>}
                            {team.map((p) => (
                                <button
                                    key={p.id}
                                    className="ap-row"
                                    disabled={assignMut.isPending}
                                    onClick={() => assignMut.mutate({ payload: assignFor, assigneeId: p.id })}
                                >
                                    <span
                                        className="ap-a"
                                        style={{ background: p.role === "admin" ? "#6b5b95" : "#3f7d6e" }}
                                    >
                                        {(p.name || "?").charAt(0)}
                                    </span>
                                    <span className="ap-n">
                                        {p.name || "Unnamed"}
                                        <small>{p.role}</small>
                                    </span>
                                    <span className="ap-go">→</span>
                                </button>
                            ))}
                        </div>
                        {assignMut.isError && <div className="ap-err">Couldn't assign — try again.</div>}
                        <button className="ap-cancel" onClick={() => setAssignFor(null)}>
                            Cancel
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}

function pct(done?: number, total?: number): number {
    if (!total || total <= 0) return 0;
    return Math.min(100, Math.round(((done ?? 0) / total) * 100));
}
function trim(s: string, max = 30): string {
    return s.length > max ? s.slice(0, max - 1).trimEnd() + "…" : s;
}

/** The admin (data-integrity) lanes — reuses the approved track/tile shells,
 *  no new CSS. Quotes / Work-order mapping / Report mapping, plus doc-fixes. */
function AdminLanes({
    aq,
    trackStyle,
    personalHint,
    navigate,
}: {
    aq: AdminBrief | null;
    trackStyle: (lane: string) => { order: number };
    personalHint: string;
    navigate: (to: string) => void;
}) {
    const quotes = aq?.quotes.count ?? 0;
    const blocking = aq?.workOrders.blockingCount ?? 0;
    const teach = aq?.workOrders.teachCount ?? 0;
    const held = aq?.reports.heldCount ?? 0;
    const bounced = aq?.reports.rejectedCount ?? 0;
    return (
        <>
            <div className="tracks-h">
                YOUR LANES · <b>{personalHint}</b>
            </div>
            <div className="tracks">
                <div className="track dispatch" style={trackStyle("quotes")}>
                    <div className="k">Quotes</div>
                    <div className="n">{quotes}</div>
                    <div className="bar">
                        <i style={{ width: `${quotes ? 72 : 8}%` }} />
                    </div>
                    <div className="note">
                        {aq?.quotes.top[0] ? (
                            <>
                                Oldest: <b>{trim(aq.quotes.top[0].label, 26)}</b> · {aq.quotes.top[0].blockedDays}d
                            </>
                        ) : (
                            <>Every quote reviewed.</>
                        )}
                    </div>
                    <span className="flag">{quotes} to review</span>
                </div>
                <div className="track prep" style={trackStyle("workOrders")}>
                    <div className="k">Work Orders</div>
                    <div className="n">{blocking}</div>
                    <div className="bar">
                        <i style={{ width: `${blocking ? 60 : 8}%` }} />
                    </div>
                    <div
                        className="note"
                        title="Blocking = a form field the system can't fill is holding a job's paperwork. Need teaching = the form mapper needs to be shown where that field's value comes from (Fix future forms)."
                    >
                        <b>{blocking} blocking</b> a job · {teach} need teaching.
                    </div>
                    <span className="flag">{blocking} to map</span>
                </div>
                <div className="track closeout" style={trackStyle("reports")}>
                    <div className="k">Report Mapping</div>
                    <div className="n">{held}</div>
                    <div className="bar">
                        <i style={{ width: `${held ? 55 : 8}%` }} />
                    </div>
                    <div className="note">
                        <b>{held} held</b> for a device · {bounced} bounced by the city.
                    </div>
                    <span className="flag">{held} held</span>
                </div>
            </div>

            <div className="tiles">
                <div className="tile">
                    <div className="th">
                        <span className="name">Doc fixes</span>
                        <span className="cnt">{aq?.docFixes.count ?? 0}</span>
                    </div>
                    <div className="body">
                        {(aq?.docFixes.top ?? []).map((d) => (
                            <div className="row" key={d.id}>
                                <span>{trim(d.label)}</span>
                                <button
                                    className="go"
                                    style={{ border: 0, cursor: "pointer" }}
                                    onClick={() => navigate(d.href)}
                                >
                                    fix
                                </button>
                            </div>
                        ))}
                        {(aq?.docFixes.count ?? 0) === 0 && <span className="empty">✓ No doc fixes waiting.</span>}
                    </div>
                </div>
                <div className="tile">
                    <div className="th">
                        <span className="name">Work orders to map</span>
                        <span className="cnt">{blocking}</span>
                    </div>
                    <div className="body">
                        {(aq?.workOrders.top ?? []).map((w) => (
                            <div className="row" key={w.id}>
                                <span>
                                    {trim(w.label, 22)}{" "}
                                    <span style={{ color: "var(--muted-2)" }}>· {trim(w.reason, 18)}</span>
                                </span>
                                <button
                                    className="go"
                                    style={{ border: 0, cursor: "pointer" }}
                                    onClick={() => navigate(w.href)}
                                >
                                    map
                                </button>
                            </div>
                        ))}
                        {blocking === 0 && <span className="empty">✓ No work orders blocked on mapping.</span>}
                    </div>
                </div>
            </div>
        </>
    );
}

/** CC-B — the Running-mode pace prediction, rendered as the hero. Only shown
 *  when a truck is genuinely behind plan (server-derived, never faked). The
 *  prepped options route to The Grid, where the reassign/reschedule happens —
 *  this page owns no dispatch mutation. */
function PredictionHero({
    pred,
    navigate,
    onLetRide,
}: {
    pred: RunPrediction;
    navigate: (to: string) => void;
    onLetRide: () => void;
}) {
    const n = pred.atRisk.length;
    const stops = pred.atRisk.map((s) => s.label).join(" + ");
    return (
        <div className="card hero">
            <div className="eyebrow">
                RUNNING · PREDICTION <span className="rest">· catch it now, not at 4 PM</span>
            </div>
            <span
                className="waiting"
                title="A forecast from the tech's pace so far — nothing has actually been missed yet"
            >
                predicted, not yet real
            </span>
            <span
                className="tag"
                style={{ background: "var(--red-soft)", color: "var(--red)" }}
                title="Pace alert — this tech's actual speed vs their planned schedule"
            >
                PACE
            </span>
            <h2>
                {pred.techName} is {pred.slipMinutes} minutes behind —{" "}
                {n === 1 ? "the last stop won't" : `${n} stops won't`} happen at this pace
            </h2>
            <div className="money">
                Stop {pred.stopN} of {pred.stopM} is running late
                {pred.currentLabel ? ` (${pred.currentLabel})` : ""}. At risk: <b>{stops}</b>.
            </div>
            <div className="actions">
                <button className="btn-do" onClick={() => navigate(pred.gridHref)}>
                    {pred.absorbTech ? `Shift ${n} to ${pred.absorbTech} →` : "Reassign on The Grid →"}
                </button>
                <button className="btn-assign" onClick={() => navigate(pred.gridHref)}>
                    Reschedule
                </button>
                {/* #828 m5: plain words on the dismiss — "let it ride" was
                    poker slang a new hire had to guess at. */}
                <button
                    className="btn-skip"
                    onClick={onLetRide}
                    title="Dismiss this prediction and do nothing — it won't be shown again today"
                >
                    Do nothing for now
                </button>
                <span className="route">→ {pred.gridHref}</span>
            </div>
        </div>
    );
}

/** CC-B — the live per-truck strip (Running mode). Dot + status + pace vs plan
 *  for every truck out today; a behind truck shows a red dot and red pace line,
 *  an idle/finished truck a grey dot. */
function LiveField({ trucks }: { trucks: TruckPace[] }) {
    return (
        <div className="card livefield">
            <div className="lf-h">
                <span>Live field</span>
                <span className="lf-sub">
                    {trucks.length} truck{trucks.length === 1 ? "" : "s"} · pace vs plan
                </span>
            </div>
            <div className="lf-grid">
                {trucks.map((t) => {
                    const dotClass = t.dot === "late" ? "late" : t.dot === "idle" || t.dot === "waiting" ? "idle" : "";
                    return (
                        <div className="truck" key={t.techName}>
                            <div className="who">
                                <span className={`dot ${dotClass}`} />
                                {t.techName}
                            </div>
                            <div className="st">{t.statusLine}</div>
                            <div className={`pace ${t.paceBad ? "bad" : ""}`}>{t.paceLine}</div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

/**
 * Deadline radar (#2096). Was hardcoded to "0 · No live deadlines" while the
 * server sent real rows. Now: every row the server sends, soonest first, red
 * when out of date and amber inside 30 days; each row opens its fix page.
 */
export function DeadlineRadarTile({ rows, onOpen }: { rows: DeadlineRow[]; onOpen: (href: string) => void }) {
    const today = orgTodayKey();
    const outOfDate = rows.filter((r) => daysLeftOf(r, today) < 0).length;
    const SHOWN = 6;
    return (
        <div className="tile" data-testid="deadline-radar">
            <div className="th">
                <span className="name">Deadline radar</span>
                <span className={`cnt${rows.length === 0 ? " ok" : outOfDate > 0 ? " red" : " amber"}`}>
                    {rows.length}
                </span>
            </div>
            <div className="body">
                {rows.length === 0 ? (
                    <span className="empty">✓ Nothing due in the next 30 days.</span>
                ) : (
                    <>
                        {rows.slice(0, SHOWN).map((r, i) => {
                            const left = daysLeftOf(r, today);
                            return (
                                <button
                                    type="button"
                                    key={`${r.label}-${i}`}
                                    className={`row radar ${rowTone(left)}`}
                                    data-testid="deadline-radar-row"
                                    onClick={() => onOpen(r.href)}
                                >
                                    <span>{r.label}</span>
                                    <b>
                                        {shortDue(r.due_on, today)} · {dueWords(left)}
                                    </b>
                                </button>
                            );
                        })}
                        {rows.length > SHOWN && (
                            <button type="button" className="row radar more" onClick={() => onOpen("/compliance")}>
                                <span>+{rows.length - SHOWN} more</span>
                                <b>Compliance →</b>
                            </button>
                        )}
                    </>
                )}
            </div>
        </div>
    );
}
